import { ReactNode, memo } from "react";
import { Link as ReactRouterLink } from "./Link";
import { RouteStore, IMenuRoute } from "./RouteStore";
import { useSingleton } from "../injection";
import { observer } from "mobx-react-lite";
import { LoggedInUserStore } from "../loggedInUser/LoggedInUserStore";
import { useTheme } from "../../other/theme";
import styled from "@emotion/styled";
import { level35 } from "../../other/theme/opacities";
import UserIconRaw from "./user-icon.svg";
import SettingIconRaw from "./setting-icon.svg";
import AdminIconRaw from "./admin-icon.svg";
import { IconBuilder } from "../../uiLibrary/icons/builder/IconBuilder";
import {
    IRoute,
    settingsRoute,
    userRoute,
    adminRoute,
    mainNavRoutes
} from "./routes";
import { useLink } from "./useLink";
import { SystemMonitorResultStatus } from "../../features/systemMonitor/SystemMonitorResultStatusComponent";
import { useLocation } from "react-router-dom";
import BrandLogo from "@/branding/BrandLogo";

export const Nav = observer(() => {
    const routeStore = useSingleton(RouteStore);
    const loggedInUserStore = useSingleton(LoggedInUserStore);

    const location = useLocation();
    const currentRoute = routeStore.getRouteByPath(location.pathname);

    const currentAncestor = routeStore.getTopAncestor(currentRoute, undefined);

    const isAdminPermissioned = routeStore.getIsRoutePermissioned(adminRoute);
    const isSettingsPermissioned =
        routeStore.getIsRoutePermissioned(settingsRoute);
    const isUserPermissioned = routeStore.getIsRoutePermissioned(userRoute);

    const mainRoutes = routeStore.getMenuRoutes(mainNavRoutes);

    const theme = useTheme();

    return (
        <Container data-testid="appNavigation">
            <Top>
                <LogoWrapper>
                    <ReactRouterLink to="/" data-testid="home">
                        <BrandLogo variant="white" />
                    </ReactRouterLink>
                </LogoWrapper>
                <Links>
                    {mainRoutes.map((x, i) => (
                        <Link
                            key={i}
                            route={x}
                            currentAncestor={currentAncestor}
                        >
                            {routeStore.getRouteTitle(x)}
                        </Link>
                    ))}
                </Links>
            </Top>
            <Links>
                <SystemMonitorResultStatus />
                {isAdminPermissioned && (
                    <Link route={adminRoute} currentAncestor={currentAncestor}>
                        <Username>
                            {typeof adminRoute.title === "string" &&
                                adminRoute.title}
                        </Username>
                        <LinkIcon>
                            <AdminIcon
                                size={12}
                                color={theme.brandForeground}
                            />
                        </LinkIcon>
                    </Link>
                )}
                {isSettingsPermissioned && (
                    <Link
                        route={settingsRoute}
                        currentAncestor={currentAncestor}
                    >
                        <Username>
                            {typeof settingsRoute.title === "string" &&
                                settingsRoute.title}
                        </Username>
                        <LinkIcon>
                            <SettingIcon
                                size={12}
                                color={theme.brandForeground}
                            />
                        </LinkIcon>
                    </Link>
                )}
                {isUserPermissioned && (
                    <Link
                        route={userRoute}
                        currentAncestor={currentAncestor}
                        isImpersonating={loggedInUserStore.isImpersonating}
                    >
                        <Username>{loggedInUserStore.username}</Username>
                        <LinkIcon>
                            <UserIcon size={12} color={theme.brandForeground} />
                        </LinkIcon>
                    </Link>
                )}
            </Links>
        </Container>
    );
});

// eslint-disable-next-line react-compiler/react-compiler
const Link = memo(function MemoLink({
    route,
    currentAncestor,
    isImpersonating,
    children
}: {
    route: IMenuRoute;
    currentAncestor: IRoute | undefined;
    isImpersonating?: boolean;
    children: ReactNode;
}) {
    "use no memo";

    const isSelected =
        currentAncestor !== undefined &&
        (currentAncestor === route || currentAncestor === route.originalRoute);

    let link = useLink({ route });
    if (route.useNavigationLink) {
        // eslint-disable-next-line react-compiler/react-compiler
        link = route.useNavigationLink();
    }

    return route.path ? (
        <a
            href={link.href}
            target={link.target}
            onClick={link.onClick}
            data-testid={link.testId}
        >
            <Li isSelected={isSelected} isImpersonating={isImpersonating}>
                <span>{children}</span>
            </Li>
        </a>
    ) : null;
});

const Container = styled("div")(props => ({
    flexShrink: 0,
    width: 150,
    backgroundColor: props.theme.brandDeep,
    display: "flex",
    flexDirection: "column",
    padding: "18px 10px 10px 10px",
    overflow: "hidden"
}));

const Top = styled("div")({
    flexGrow: 1
});

const LogoWrapper = styled("div")({
    paddingLeft: 10
});

const Links = styled("ul")(props => ({
    marginTop: 20,
    marginLeft: 2,
    "> a": {
        textDecoration: "none",
        color: props.theme.brandForeground
    }
}));

const Li = styled("li")<{ isSelected: boolean; isImpersonating?: boolean }>(
    props => ({
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "flex-start",
        minHeight: 30,
        padding: "4px 7px",
        cursor: "pointer",
        backgroundColor: props.isImpersonating
            ? props.theme.error
            : props.isSelected
              ? props.theme.brandMedium + level35
              : undefined,
        borderRadius: 1,
        "&:hover": {
            backgroundColor:
                props.isSelected || props.isImpersonating
                    ? undefined
                    : props.theme.brandMedium + level35,
            span: {
                opacity: 1,
                color: "inherit"
            }
        },
        span: {
            display: "inline-flex",
            flexDirection: "row",
            justifyContent: "center",
            fontSize: props.theme.fontSizes.small,
            opacity: props.isSelected || props.isImpersonating ? 1 : 0.8
        }
    })
);

const LinkIcon = styled("div")({
    fontSize: 1 // icon is smaller than the parent font, so without this, there is unwanted padding underneath the icon
});

const SettingIcon = IconBuilder({
    svg: SettingIconRaw,
    defaultColor: theme => theme.foreground
});

const AdminIcon = IconBuilder({
    svg: AdminIconRaw,
    defaultColor: theme => theme.foreground
});

const UserIcon = IconBuilder({
    svg: UserIconRaw,
    defaultColor: theme => theme.foreground
});

const Username = styled("div")({
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    width: 100 // hacky way in getting break-word to work. can't seem to get this element to have an automatically calculated width using flex box
});
